/**
 * The patient record read as a SEQUENCE — the "Dossier" tab.
 *
 * Two blocks, and they do not divide the fields between them — they ask two different questions
 * about all of them:
 *
 * - **the profile** — *what is known about this patient*. Every clinical field, as the dated
 *   projection has it at the cursor's date. Nothing is withheld from it, and `Renseignés / Tous`
 *   says what the record is silent about.
 * - **the episodes** — *what each moment established*. Under an episode, the fields its own
 *   documents actually stated, whatever the ontology calls them.
 *
 * A field appearing in both is not a duplication, it is the point: on a revised patient the profile
 * says `TLIF · L3-L4` because that is the current state, and the first episode says
 * `arthrodèse cervicale · C5-C6` because that is what that operation was.
 *
 * ## Why the split is not `temporal_role`
 *
 * It used to be: empty role → profile, `current`/`attribute` → episode. Measured on the demo tenant,
 * that column is the wrong discriminator. Across the 55 dossiers carrying two episodes,
 * `traitements_chirurgical` differs between them 52 times and `surgical_measures_decompression` 41 —
 * but so does `diagnostics_principaux`, 28 times, and its role is EMPTY. Meanwhile `score_asa`
 * carries `attribute` and never differs once. Splitting on that column showed a half-filled ontology
 * column as if it were a clinical rule, and made the page read as though only surgical technique and
 * complications belonged to an episode.
 *
 * So the episode asks the registry, not the ontology: it shows the fields its moments carried. The
 * ontology keeps deciding LABELS and SECTIONS — the things it actually knows.
 *
 * ## One renderer, everywhere
 *
 * This component draws no value of its own. Every field — profile and episode alike — goes through
 * the page's `ValueAndPropertyDisplay` via `renderField`, so all of them keep the same encart, scale,
 * hover chart, pencil and check. What differs is the date a correction describes: the cursor's day in
 * the profile, the day of the moment that established the value inside an episode.
 */
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { msg } from "@lingui/macro";
import dayjs from "dayjs";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Stethoscope,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@app/components/ui/card";
import type { PatientSchema } from "@app/types/few-schot-schema";
import { useSourceDocument } from "@app/utils/source-document";
import { SourceDocumentLink } from "@app/components/SourceDocument";
import type { Passage } from "@app/components/ValuePassages";
import {
  DECISION_LABELS,
  OUTCOME_LABELS,
  OUTCOME_TONES,
} from "@app/utils/care-pathway-labels";
import {
  documentDates,
  episodeKey,
  gestureLabel,
  indicationLabel,
  isBlank,
  lastMomentOf,
  problemFieldKeys,
  profileFieldKeys,
  projectPathway,
  sectionsFor,
  type ChronologyItem,
  type EpisodeField,
  type PathwayResponse,
  type ProjectedEpisode,
} from "@app/utils/care-pathway";
import type { ServedGesture } from "@app/utils/resolve-field-value";

// An episode served before the server placed its gestures shows none. One object, so the memo of the
// rows it feeds does not see a new one at every render.
const NO_GESTURES: Readonly<Record<string, ServedGesture>> = {};

/** Renders one ontology field with the page's own editable row.
 *
 *  `override` is what makes an episode's value renderable by the SAME component as a profile value:
 *  the row normally reads the field off the projected patient, and an episode needs it to read the
 *  value that moment established instead — dated by that moment, so a correction lands in the journal
 *  on the right day rather than today. */
export type RenderFieldFn = (
  fieldKey: string,
  override?: {
    value: unknown;
    effectiveDate: string | null;
    labelSuffix?: string;
    /** The document whose event established this moment — the SCOPE a correction made here
     *  is about. The date says from when the value holds; this says what it is a statement
     *  about, and an episode boundary is an interval a date alone cannot be placed against. */
    scopeDocId?: string | null;
    /** The sentences this episode's value was read from, most recent first. */
    passages?: Passage[];
    /** The clinician's gesture this row shows, as the server placed it (`GET /pathway`). */
    gesture?: ServedGesture | null;
  },
) => React.ReactNode;

/** One field of an episode AS THE BLOCK DRAWS IT: the value its last moment established, the day
 *  that moment happened, and the document that established it.
 *
 *  It is the `override` of `RenderFieldFn` plus the key it is drawn for. Exported because a second
 *  reader needs exactly the same list: the bar that closes the episode in one gesture. Recomputing
 *  it there from the pathway would be a second reading of the same thing, and the two would drift
 *  on the case that matters — a field the block shows but the episode never stated, which "Tous"
 *  lists and which a sweep must confirm as an absence rather than skip. */
export interface EpisodeMoment {
  fieldKey: string;
  value: unknown;
  effectiveDate: string | null;
  labelSuffix?: string;
  scopeDocId: string | null;
  passages: Passage[];
  gesture: ServedGesture | null;
}

/** The bar that closes ONE episode, rendered by the page under the fields it sweeps. Given the
 *  episode's id and the moments currently on screen — filter included, because what is swept is
 *  what was read. */
export type RenderEpisodeReviewFn = (
  episodeId: string,
  moments: EpisodeMoment[],
) => React.ReactNode;

type FieldFilter = "filled" | "all";

function formatMonth(value: string | null | undefined): string {
  return value && dayjs(value).isValid() ? dayjs(value).format("MMM YYYY") : "—";
}

/** A section of the ontology, inside a card. */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-slate-400">
      {children}
    </h4>
  );
}

function FilterChip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
        on
          ? "border-primary bg-primary/10 text-primary"
          : "border-slate-200 bg-white text-slate-600 hover:border-primary/50"
      }`}
    >
      {children}
    </button>
  );
}

/** What the blue banner carries — see `PatientHeaderCard.badgesSlot`.
 *
 *  `header_role` declares the indication, and that is the right FIELD: what a surgeon wants at the
 *  top of a record is what this patient is, clinically. What was wrong is the SCOPE — read off the
 *  flat row it became the union of every episode's reason, so a revised patient carried two
 *  contradictory diagnoses side by side. Read off ONE episode, it is one diagnosis followed by the
 *  act it led to.
 *
 *  The episode is the reader's: the one unfolded below, the latest when none is. The frame names it,
 *  rank and dates, because a header that follows the selection would otherwise show a past
 *  episode's diagnosis as the patient's current one. The outcome stays on the episode's row only.
 *
 *  Its chevrons step through the episodes by opening them in the pathway: the frame and the rows
 *  below read one selection. They sit before the dates so they do not move as the frame resizes. */
export function PathwaySummary({
  episodes,
  openEpisodeId,
  onOpenEpisode,
  schema,
}: {
  episodes: ProjectedEpisode[];
  /** The episode unfolded in the pathway, or `null` when every episode is folded. */
  openEpisodeId: string | null;
  onOpenEpisode: (episodeId: string) => void;
  /** Names the indication and the act in the reader's language. */
  schema: PatientSchema;
}) {
  const { t } = useLingui();
  if (episodes.length === 0) return null;
  const open = episodes.find((episode) => episode.episode.id === openEpisodeId);
  const shown = open ?? episodes[episodes.length - 1];
  const index = episodes.indexOf(shown);
  const previous = episodes[index - 1];
  const next = episodes[index + 1];
  const levels = shown.levels.map((level) => level.label).join(" · ");
  return (
    <div
      className={`w-fit max-w-full rounded-lg border px-4 py-3 transition-colors ${
        open ? "border-primary/40 bg-primary/5" : "border-slate-200 bg-white"
      }`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-x-2 text-2xs text-slate-400">
        {episodes.length > 1 && (
          <span className="flex items-center gap-1">
            <EpisodeStep
              label={t`Épisode précédent`}
              target={previous}
              onOpenEpisode={onOpenEpisode}
            >
              <ChevronLeft className="h-4 w-4" />
            </EpisodeStep>
            <span className="font-medium uppercase tracking-wide tabular-nums">
              <Trans>
                Épisode {shown.episode.rank} sur {episodes.length}
              </Trans>
            </span>
            <EpisodeStep
              label={t`Épisode suivant`}
              target={next}
              onOpenEpisode={onOpenEpisode}
            >
              <ChevronRight className="h-4 w-4" />
            </EpisodeStep>
          </span>
        )}
        <span className="tabular-nums">
          {formatMonth(shown.openedOn)}
          {shown.closedOn ? ` → ${formatMonth(shown.closedOn)}` : ""}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-lg bg-gradient-to-r from-primary to-primary/90 px-4 py-1.5 text-sm font-medium text-white shadow-sm">
          {indicationLabel(shown.episode, schema) ?? (
            <Trans>Indication non nommée</Trans>
          )}
        </span>
        {(shown.gesture || shown.actUnnamed) && (
          <span className="rounded-lg bg-blue-100 px-3 py-1.5 text-sm font-medium text-blue-900">
            {gestureLabel(shown, schema) ?? <Trans>Geste non nommé</Trans>}
            {levels ? ` · ${levels}` : ""}
          </span>
        )}
      </div>
    </div>
  );
}

/** One chevron of the frame. Disabled at either end of the record rather than wrapping around: from
 *  the latest episode, a step that lands on the first one would read as going forward in time. */
function EpisodeStep({
  label,
  target,
  onOpenEpisode,
  children,
}: {
  label: string;
  target: ProjectedEpisode | undefined;
  onOpenEpisode: (episodeId: string) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => target && onOpenEpisode(target.episode.id)}
      disabled={!target}
      aria-label={label}
      title={label}
      className="rounded-md p-0.5 text-slate-500 transition-colors hover:bg-primary/10 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:text-slate-300"
    >
      {children}
    </button>
  );
}

/** The latest news of the patient, on the identity line — see `PatientHeaderCard.detailsSlot`.
 *  The last episode's, whichever one the frame shows. */
export function PathwayLastContact({
  episodes,
}: {
  episodes: ProjectedEpisode[];
}) {
  const last = episodes[episodes.length - 1];
  if (!last?.lastContactOn) return null;
  const lastContact = dayjs(last.lastContactOn).format("DD MMM YYYY");
  return (
    <span className="flex items-center gap-1.5">
      <Clock className="h-4 w-4 text-slate-400" aria-hidden />
      <Trans>Dernières nouvelles le {lastContact}</Trans>
    </span>
  );
}

interface CarePathwayProps {
  pathway: PathwayResponse | null;
  schema: PatientSchema;
  /** The dated projection at the cursor position — the source of every profile value. */
  projectedFields: Record<string, unknown> | null;
  /** Cursor position (ISO day) or `null` for today. */
  at: string | null;
  /** The practitioner's PERSONAL field selection (``zustand/field-visibility``). Applied to the
   *  profile AND to an episode's declared list, so a field switched off disappears from both — a
   *  preference that held on the flat page and stopped holding here would read as a bug. */
  hiddenKeys?: ReadonlySet<string>;
  /** Read-only role. Only the practitioner's name needs it here — every VALUE on this page is
   *  rendered by ``ValueAndPropertyDisplay``, which blurs itself. */
  /** Which episode is unfolded, or `null` for none. Owned by the page rather than here, because the
   *  chronology of that episode is rendered in the right-hand column: two components read the same
   *  selection. */
  openEpisodeId: string | null;
  onOpenEpisode: (episodeId: string | null) => void;
  /** Open every selector on "Tous" rather than on what the record filled. A reader who came to
   *  judge the extraction needs the silences as much as the values — an empty field is a miss, and
   *  the default filter hides exactly those. Only the DEFAULT moves: one click still restores it. */
  defaultToAllFields?: boolean;
  renderField: RenderFieldFn;
  /** See `RenderEpisodeReviewFn`. Absent on a surface with no gesture to offer. */
  renderEpisodeReview?: RenderEpisodeReviewFn;
}

export default function CarePathway({
  pathway,
  schema,
  projectedFields,
  at,
  hiddenKeys,
  openEpisodeId,
  onOpenEpisode,
  defaultToAllFields = false,
  renderField,
  renderEpisodeReview,
}: CarePathwayProps) {
  const { t } = useLingui();
  const episodes = useMemo(() => projectPathway(pathway, at), [pathway, at]);
  // Newest first: a record is opened for what just happened, and the older episodes are context.
  const ordered = useMemo(() => [...episodes].reverse(), [episodes]);

  const episodeFields = useMemo(() => new Set(pathway?.episode_fields ?? []), [pathway]);
  const docDates = useMemo(() => documentDates(pathway), [pathway]);
  // The patient's TERRAIN: everything that is not about an act. Verified rather than assumed — on the
  // 55 demo dossiers carrying two episodes, not one field in this list differs between them, while
  // every field it excludes differs somewhere between 1 and 52 times. A block whose contents never
  // distinguish one episode from another is exactly what belongs above the pathway rather than inside
  // it.
  const profileKeys = useMemo(
    () => profileFieldKeys(pathway, schema, hiddenKeys),
    [pathway, schema, hiddenKeys],
  );
  const problemKeys = useMemo(
    () => problemFieldKeys(pathway, schema, hiddenKeys),
    [pathway, schema, hiddenKeys],
  );

  if (!pathway) return null;
  const openProjected = ordered.find(
    (projected) => projected.episode.id === openEpisodeId,
  );
  const describesDecision =
    !pathway.natives || pathway.natives.includes("decision");

  return (
    <div className="space-y-6">
      <Card className="border-l-4 border-l-primary">
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            <span>🧭</span>
            <span>
              <Trans>Parcours de soins</Trans>
            </span>
            {ordered.length > 0 && (
              <span className="text-sm font-normal text-slate-400 tabular-nums">
                {ordered.length}{" "}
                {ordered.length > 1 ? <Trans>épisodes</Trans> : <Trans>épisode</Trans>}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {ordered.length === 0 ? (
            <p className="px-6 pb-6 text-sm text-slate-500">
              {at ? (
                <Trans>Aucun épisode n'était ouvert à cette date.</Trans>
              ) : (
                <Trans>
                  Aucun épisode reconstruit pour ce dossier. Les valeurs restent
                  lisibles dans le profil ci-dessous.
                </Trans>
              )}
            </p>
          ) : (
            <>
              <div
                data-tour="patient-pathway"
                className="border-t border-slate-200"
              >
                {ordered.map((projected) => (
                  <EpisodeRow
                    key={episodeKey(projected.episode)}
                    projected={projected}
                    schema={schema}
                    open={projected.episode.id === openEpisodeId}
                    onToggle={() =>
                      onOpenEpisode(
                        projected.episode.id === openEpisodeId
                          ? null
                          : projected.episode.id,
                      )
                    }
                    decisionLabel={
                      projected.decision
                        ? t(DECISION_LABELS[projected.decision] ?? msg`—`)
                        : null
                    }
                    outcomeLabel={
                      projected.outcome
                        ? t(OUTCOME_LABELS[projected.outcome] ?? msg`—`)
                        : null
                    }
                    describesDecision={describesDecision}
                  />
                ))}
              </div>
              {/* Under the whole list rather than under its own row. Inline, an open episode pushed
                  every older one a screen of fields down, and the header's « Épisode 2 sur 2 » named
                  an episode the reader could not see. The rows stay together as the record's index. */}
              {openProjected && (
                <EpisodeDetail
                  // Keyed like the selection: by id, every gesture remounted it and its « Renseignés
                  // / Tous » filter fell back to the default.
                  key={episodeKey(openProjected.episode)}
                  projected={openProjected}
                  schema={schema}
                  episodeFields={episodeFields}
                  docDates={docDates}
                  hiddenKeys={hiddenKeys}
                  renderField={renderField}
                  renderEpisodeReview={renderEpisodeReview}
                  defaultToAllFields={defaultToAllFields}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* AFTER the pathway, deliberately. A specialist opens a record to answer "what happened and
          what did we do"; the profile is what they consult WHILE reading that, not before reaching
          it. Measured on the demo cohort, the profile carries 27 to 29 filled rows and an episode's
          default filter carries 5 to 14 — putting the twenty-nine ahead of the fourteen that
          distinguish is the wall this page exists to remove. The blue banner already answers "who is
          this, clinically" at the top, so nothing is lost by reading the terrain second. */}
      {problemKeys.length > 0 && (
        <PatientFieldsCard
          keys={problemKeys}
          schema={schema}
          values={projectedFields}
          defaultToAllFields={defaultToAllFields}
          renderField={renderField}
          emoji="🩺"
          title={<Trans>Problèmes et traitements</Trans>}
          emptyLabel={<Trans>Aucun problème ni traitement à cette date.</Trans>}
          foldLabel={t`Replier les problèmes et traitements`}
          unfoldLabel={t`Déplier les problèmes et traitements`}
        />
      )}
      <PatientFieldsCard
        keys={profileKeys}
        schema={schema}
        values={projectedFields}
        defaultToAllFields={defaultToAllFields}
        renderField={renderField}
        emoji="👤"
        title={<Trans>Profil</Trans>}
        emptyLabel={<Trans>Aucune donnée de profil à cette date.</Trans>}
        foldLabel={t`Replier le profil`}
        unfoldLabel={t`Déplier le profil`}
      />
    </div>
  );
}

/** A patient-level card: the PROFILE — who the person is, their measurements and the score
 *  trajectories that run across the whole record — or their PROBLEMS and TREATMENTS, the declared
 *  problem lists and the sections that hold them. Neither belongs to any one act.
 *
 *  Which fields land here is verified rather than assumed: on the 55 demo dossiers carrying two
 *  episodes, not one of them differs between the two, while every field the pathway keeps differs
 *  somewhere between 1 and 52 times. The ontology fields attached to no act, scores included — they are the only
 *  fields that cross episodes, so the whole-record trajectory belongs here rather than under any one
 *  of them. Foldable, because on a speciality declaring thirty of them it is a long block a reader
 *  scrolls past on the way to the pathway. */
function PatientFieldsCard({
  keys,
  schema,
  values,
  defaultToAllFields = false,
  renderField,
  emoji,
  title,
  emptyLabel,
  foldLabel,
  unfoldLabel,
}: {
  keys: string[];
  schema: PatientSchema;
  values: Record<string, unknown> | null;
  defaultToAllFields?: boolean;
  renderField: RenderFieldFn;
  emoji: string;
  title: ReactNode;
  emptyLabel: ReactNode;
  foldLabel: string;
  unfoldLabel: string;
}) {
  const [open, setOpen] = useState(true);
  // `null` = the reader has not chosen, so the default applies — and keeps applying if it arrives
  // late. Seeding the state from the prop instead would freeze the first render's answer, which is
  // "no dataset" for as long as that list is being fetched.
  const [chosen, setChosen] = useState<FieldFilter | null>(null);
  const filter = chosen ?? (defaultToAllFields ? "all" : "filled");
  const filled = keys.filter((key) => !isBlank(values?.[key]));
  const shown = filter === "all" ? keys : filled;
  const sections = useMemo(() => sectionsFor(shown, schema), [shown, schema]);

  return (
    <Card className="border-l-4 border-l-primary">
      <CardHeader className="pb-4">
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span>{emoji}</span>
          <span>{title}</span>
          <span className="ml-auto flex flex-wrap items-center gap-2.5">
            {open && (
              <>
                <FilterChip on={filter === "filled"} onClick={() => setChosen("filled")}>
                  <Trans>Renseignés</Trans> · {filled.length}
                </FilterChip>
                <FilterChip on={filter === "all"} onClick={() => setChosen("all")}>
                  <Trans>Tous</Trans> · {keys.length}
                </FilterChip>
              </>
            )}
            <button
              type="button"
              onClick={() => setOpen((value) => !value)}
              className="rounded-md p-1 text-slate-400 hover:text-primary"
              aria-label={open ? foldLabel : unfoldLabel}
            >
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </span>
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent>
          {shown.length === 0 ? (
            <p className="text-sm text-slate-500">{emptyLabel}</p>
          ) : (
            sections.map((section) => (
              <section key={section.name}>
                <SectionTitle>
                  {section.emoji ? `${section.emoji} ` : ""}
                  {section.name}
                </SectionTitle>
                {section.keys.map((key) => (
                  <div key={key}>{renderField(key)}</div>
                ))}
              </section>
            ))
          )}
        </CardContent>
      )}
    </Card>
  );
}

/** One line of the record's index: indication, decision, act, outcome, dates. Selecting it shows
 *  the episode's fields under the list and in the header's frame. */
function EpisodeRow({
  projected,
  schema,
  open,
  onToggle,
  decisionLabel,
  outcomeLabel,
  describesDecision,
}: {
  projected: ProjectedEpisode;
  schema: PatientSchema;
  open: boolean;
  onToggle: () => void;
  decisionLabel: string | null;
  outcomeLabel: string | null;
  describesDecision: boolean;
}) {
  const { episode, levels, gesture } = projected;
  const levelLabel = levels.map((level) => level.label).join(" · ");
  const gestureName = gestureLabel(projected, schema);

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={`flex w-full flex-wrap items-center gap-2 border-b border-slate-200 px-6 py-3 text-left transition-colors last:border-b-0 ${
        open ? "bg-blue-50" : "bg-white hover:bg-slate-50"
      }`}
    >
      {open ? (
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
      ) : (
        <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
      )}
      <span className="shrink-0 rounded border border-slate-200 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-slate-500">
        <Trans>Ép.</Trans> {episode.rank}
      </span>
      <span className="text-sm font-semibold text-slate-900">
        {indicationLabel(episode, schema) ?? (
          <Trans>Indication non nommée</Trans>
        )}
      </span>
      {describesDecision && (
        <>
          <Arrow />
          {decisionLabel ? (
            <span className="text-sm text-slate-700">{decisionLabel}</span>
          ) : (
            <NotYet>
              <Trans>pas encore décidé</Trans>
            </NotYet>
          )}
        </>
      )}
      {(gesture || projected.actUnnamed) && (
        <>
          <Arrow />
          <span className="text-sm font-medium text-slate-900">
            {gestureName ?? <Trans>Geste non nommé</Trans>}
            {levelLabel && (
              <span className="font-normal text-slate-500">
                {" "}
                · {levelLabel}
              </span>
            )}
          </span>
        </>
      )}
      {outcomeLabel && (
        <>
          <Arrow />
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium ${
              OUTCOME_TONES[projected.outcome ?? ""] ??
              "bg-slate-100 text-slate-600"
            }`}
          >
            {outcomeLabel}
          </span>
        </>
      )}
      <span className="ml-auto shrink-0 text-xs text-slate-400 tabular-nums">
        {formatMonth(projected.openedOn)}
        {projected.closedOn ? ` → ${formatMonth(projected.closedOn)}` : ""}
      </span>
    </button>
  );
}

/** The selected episode's fields, below the index. Its first line names the episode: under a list,
 *  the panel sits right after whichever row is last, and on a two-episode record that is the one NOT
 *  selected. */
function EpisodeDetail({
  projected,
  schema,
  episodeFields,
  docDates,
  hiddenKeys,
  renderField,
  renderEpisodeReview,
  defaultToAllFields = false,
}: {
  projected: ProjectedEpisode;
  schema: PatientSchema;
  episodeFields: ReadonlySet<string>;
  docDates: ReadonlyMap<string, string>;
  hiddenKeys?: ReadonlySet<string>;
  renderField: RenderFieldFn;
  renderEpisodeReview?: RenderEpisodeReviewFn;
  defaultToAllFields?: boolean;
}) {
  const { episode, fields } = projected;
  return (
    <div
      data-tour="patient-episode"
      className="border-t border-slate-200 bg-slate-50/60 px-6 py-4"
    >
      <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
        <span className="font-medium uppercase tracking-wide text-slate-400">
          <Trans>Ép. {episode.rank}</Trans>
        </span>
        {episode.practitioner_label && (
          <span className="flex items-center gap-1.5">
            <Stethoscope className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{episode.practitioner_label}</span>
          </span>
        )}
      </p>
      <EpisodeFields
        fields={fields}
        docDates={docDates}
        episodeFields={episodeFields}
        hiddenKeys={hiddenKeys}
        schema={schema}
        renderField={renderField}
        renderEpisodeReview={renderEpisodeReview}
        episodeId={episode.id}
        from={projected.openedOn}
        gestures={episode.gestures ?? NO_GESTURES}
        lastMoment={lastMomentOf(projected)}
        holdsAnAct={projected.indexEvent !== null}
        defaultToAllFields={defaultToAllFields}
      />
    </div>
  );
}

/** The one chronology renderer of the page.
 *
 *  It serves both the moments of the open episode and the source's raw appointment list, because
 *  those two used to be drawn by two different hands: the agenda still carried a handwriting face
 *  (`font-caveat`), a hardcoded navy and an absolute rail sized for a full-width column. Side by side
 *  in the same card that read as two unrelated widgets rather than as one column of dates. */
export function ChronologyList({
  items,
  emptyLabel,
}: {
  items: ChronologyItem[];
  emptyLabel: React.ReactNode;
}) {
  const { t } = useLingui();
  const sourceDocument = useSourceDocument();
  if (items.length === 0) {
    return <p className="text-sm text-slate-500">{emptyLabel}</p>;
  }
  return (
    <ol className="relative space-y-4 border-l border-slate-200 pl-5">
      {items.map((item) => (
        <li key={item.id} className="relative">
          <span
            className={`absolute -left-[27px] top-1 h-3 w-3 rounded-full border-2 border-white ${
              item.emphasis ? "bg-primary" : "bg-slate-300"
            }`}
            aria-hidden
          />
          <div className="flex items-baseline justify-between gap-3">
            {sourceDocument && item.docId ? (
              <SourceDocumentLink
                to={sourceDocument.href(item.docId)}
                onOpen={() => sourceDocument.open(item.docId as string)}
                title={t`Ouvrir le document`}
                className={`inline-flex items-center gap-1.5 rounded text-left text-sm hover:text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                  item.emphasis
                    ? "font-semibold text-slate-900"
                    : "font-medium text-slate-700"
                }`}
              >
                {item.title}
                <FileText
                  className="h-3.5 w-3.5 shrink-0 text-primary/60"
                  aria-hidden
                />
              </SourceDocumentLink>
            ) : (
              <span
                className={`text-sm ${
                  item.emphasis
                    ? "font-semibold text-slate-900"
                    : "font-medium text-slate-700"
                }`}
              >
                {item.title}
              </span>
            )}
            <time className="shrink-0 text-xs text-slate-400 tabular-nums">
              {dayjs(item.date).format("DD MMM YYYY")}
            </time>
          </div>
          {item.note && (
            <p
              className="text-xs text-slate-500"
            >
              {item.note}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}

/** Everything the ontology hangs off the act, in the ontology's own sections.
 *
 *  The selector is per EPISODE, not per record: "modified" only means something inside one episode,
 *  where a field stated twice differently is a correction or an evolution and both are clinical.
 *  Across episodes a field simply describes two different operations. */
function EpisodeFields({
  fields,
  docDates,
  episodeFields,
  hiddenKeys,
  schema,
  renderField,
  renderEpisodeReview,
  episodeId,
  from,
  gestures,
  lastMoment,
  holdsAnAct,
  defaultToAllFields = false,
}: {
  fields: EpisodeField[];
  docDates: ReadonlyMap<string, string>;
  episodeFields: ReadonlySet<string>;
  hiddenKeys?: ReadonlySet<string>;
  schema: PatientSchema;
  renderField: RenderFieldFn;
  renderEpisodeReview?: RenderEpisodeReviewFn;
  episodeId: string;
  from: string;
  /** The clinician's gesture each row of the episode shows, by field — decided by the server. */
  gestures: Readonly<Record<string, ServedGesture>>;
  /** The episode's latest moment: the date and document a field it never stated is confirmed at. */
  lastMoment: { date: string; docId: string | null } | null;
  /** Whether the episode holds an act yet — an act's attributes are only expected of one that does. */
  holdsAnAct: boolean;
  defaultToAllFields?: boolean;
}) {
  const { t } = useLingui();
  // A single-episode dossier has nothing to differ from, so the filter would select everything and
  // name it "propre à cet épisode" — a label that would be true and useless. It is hidden there, and
  // the default falls back to what the episode stated.
  const [chosen, setChosen] = useState<FieldFilter | null>(null);
  const filter = chosen ?? (defaultToAllFields ? "all" : "filled");
  const byKey = useMemo(() => new Map(fields.map((field) => [field.field_key, field])), [fields]);

  const visibleFields = useMemo(
    () => fields.filter((field) => !hiddenKeys?.has(field.field_key)),
    [fields, hiddenKeys],
  );

  // "Tous" = what this episode said, PLUS what the ontology expects an act to say and this one did
  // not. Listing the second half is what makes the selector honest: an operative report silent on
  // transfusion is not an operation without one. The first half is needed too, because an episode
  // routinely establishes fields the ontology attaches to no act at all — which is exactly why the
  // rest of this component stopped filtering on that column.
  //
  // Only of an episode that holds an act. A line of infiltrations has no implant to confirm, and its
  // « Tous » used to offer the act's twenty-odd attributes all the same — validated there, they were
  // dated years before the operation they were meant for (MED 67, 6119 and 10758).
  const declared = useMemo(() => {
    const established = new Set(visibleFields.map((field) => field.field_key));
    const expected = holdsAnAct
      ? Array.from(episodeFields).filter(
          (key) => key in schema && !hiddenKeys?.has(key),
        )
      : [];
    return Array.from(new Set([...established, ...expected]));
  }, [visibleFields, episodeFields, schema, hiddenKeys, holdsAnAct]);


  const keys = useMemo(() => {
    if (filter === "all") return declared;
    return visibleFields.map((f) => f.field_key);
  }, [filter, declared, visibleFields]);

  const sections = useMemo(() => sectionsFor(keys, schema), [keys, schema]);

  // What this block DRAWS, once — the sections and not `keys`, because a field absent from the
  // schema is not rendered. The same list serves the row and the bar: what was read is what is
  // swept, to within one field that would be a field validated in the doctor's name without their
  // having had it in front of them.
  const moments = useMemo<EpisodeMoment[]>(
    () =>
      sections.flatMap((section) =>
        section.keys.map((key) => {
          const field = byKey.get(key);
          const last = field?.values[field.values.length - 1];
          return {
            fieldKey: key,
            value: field ? field.latest : null,
            // A field the episode never stated is confirmed at the episode's latest moment, not at
            // its opening: dated at the opening, a validated complication showed months before the
            // operation (MED 67, 23927), and every document of the episode read as news after it.
            effectiveDate: last?.event.occurred_lo ?? lastMoment?.date ?? from,
            // Already in hand: the pathway serves the establishing document on every moment.
            // Sending it back is what lets a correction state which act it is about instead
            // of leaving it to be re-derived from its date.
            scopeDocId: last?.event.source_doc_id ?? lastMoment?.docId ?? null,
            gesture: gestures[key] ?? null,
            passages: passagesOf(field, docDates),
            labelSuffix: field?.changed ? t`modifié` : undefined,
          };
        }),
      ),
    [sections, byKey, from, lastMoment, gestures, t, docDates],
  );
  const momentByKey = useMemo(
    () => new Map(moments.map((moment) => [moment.fieldKey, moment])),
    [moments],
  );

  return (
    <div className="mt-3">
      <div className="mb-5 flex flex-wrap gap-2.5">
        {/* Two numbers, nested: what this episode said, and everything an act can say. A third
            filter — « propres à cet épisode », the subset that differs from the other episodes — was
            tried and removed: three numbers, two of which are subsets of the third, read as three
            separate lists, and that one required knowing the other episodes to understand the one
            being read. */}
        <FilterChip on={filter === "filled"} onClick={() => setChosen("filled")}>
          <Trans>Renseignés</Trans> · {visibleFields.length}
        </FilterChip>
        <FilterChip on={filter === "all"} onClick={() => setChosen("all")}>
          <Trans>Tous</Trans> · {declared.length}
        </FilterChip>
      </div>

      {sections.map((section, sectionIndex) => (
        <section key={section.name}>
          <SectionTitle>
            {section.emoji ? `${section.emoji} ` : ""}
            {section.name}
          </SectionTitle>
          {section.keys.map((key, keyIndex) => (
            <div
              key={key}
              {...(sectionIndex === 0 && keyIndex === 0
                ? { "data-tour": "patient-value" }
                : {})}
            >
              {renderField(key, momentByKey.get(key))}
            </div>
          ))}
        </section>
      ))}

      {sections.length === 0 && (
        <p className="mt-3 text-sm text-slate-500">
          <Trans>Aucun champ pour ce filtre.</Trans>
        </p>
      )}

      {/* Close THIS episode in one gesture. Under its fields, because those are what it speaks
          about: the episode's moments, each dated by the act that established it. */}
      {renderEpisodeReview?.(episodeId, moments)}
    </div>
  );
}

/** Accents and case aside, as the backend's `fold_text`. */
function foldText(value: unknown): string {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function itemsOf(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [value];
}

/** The sentences behind the value an episode shows, most recent first, each once. A moment that
 *  stated another value of the field is that value's proof, not this one's. */
function passagesOf(
  field: EpisodeField | undefined,
  docDates: ReadonlyMap<string, string>,
): Passage[] {
  const shown = new Set(itemsOf(field?.latest).map(foldText));
  const seen = new Set<string>();
  const passages: Passage[] = [];
  for (const value of [...(field?.values ?? [])].reverse()) {
    const stated = itemsOf(value.value);
    if (!stated.every((item) => shown.has(foldText(item)))) continue;
    for (const passage of value.passages) {
      const key = `${passage.source_doc_id}:${passage.verbatim}`;
      if (seen.has(key)) continue;
      seen.add(key);
      passages.push({
        key,
        verbatim: passage.verbatim,
        sourceDocId: passage.source_doc_id,
        date: passage.doc_date ?? docDates.get(passage.source_doc_id) ?? null,
        docType: passage.doc_type,
      });
    }
  }
  return passages;
}

function Arrow() {
  return (
    <span className="text-sm text-slate-300" aria-hidden>
      →
    </span>
  );
}

/** "Not yet" is not "not known", and the difference is the whole reason the cursor exists. */
function NotYet({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-b border-dashed border-slate-300 pb-px text-sm italic text-slate-400">
      {children}
    </span>
  );
}
